export interface DocumentNamingPreferences {
  enabled: boolean;
  includeTopicTitle: boolean;
  includeSubjectPrefix: boolean;
  appendCreatedDate: boolean;
}

export const DEFAULT_DOCUMENT_NAMING_PREFERENCES: DocumentNamingPreferences = {
  enabled: false,
  includeTopicTitle: true,
  includeSubjectPrefix: true,
  appendCreatedDate: false,
};

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function normalizeTopicTitle(topicTitle: string | null | undefined): string | null {
  const normalized = normalizeWhitespace(topicTitle ?? "");
  return normalized || null;
}

function removeExistingDateSuffix(value: string): string {
  return value.replace(/\s+\(\d{4}-\d{2}-\d{2}\)$/u, "").trim();
}

function normalizeDescriptor(descriptor: string): string {
  return normalizeWhitespace(descriptor)
    .replace(/^[\s\-–—:;,.]+/u, "")
    .replace(/[\s\-–—:;,.]+$/u, "")
    .trim();
}

function splitSuggestedTitle(baseTitle: string): { subject: string | null; descriptor: string } {
  const normalized = removeExistingDateSuffix(normalizeWhitespace(baseTitle));
  if (!normalized) {
    return { subject: null, descriptor: "" };
  }

  const colonIndex = normalized.indexOf(":");
  if (colonIndex === -1) {
    return { subject: null, descriptor: normalized };
  }

  const subject = normalizeDescriptor(normalized.slice(0, colonIndex));
  const descriptor = normalizeDescriptor(normalized.slice(colonIndex + 1));
  return {
    subject: subject || null,
    descriptor: descriptor || normalized,
  };
}

function parseSubjectTokens(subject: string | null): string[] {
  if (!subject) return [];
  return subject
    .split("/")
    .map((token) => normalizeDescriptor(token))
    .filter(Boolean);
}

function formatCreatedDate(createdAt?: Date | string | number | null): string {
  const parsed = createdAt != null ? new Date(createdAt) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function resolveDocumentNamingPreferences(
  value: Partial<DocumentNamingPreferences> | null | undefined
): DocumentNamingPreferences {
  return {
    ...DEFAULT_DOCUMENT_NAMING_PREFERENCES,
    ...(value || {}),
  };
}

export function getDocumentNamingPreferencesFromProfile(
  profile: { preferences?: unknown } | null | undefined
): DocumentNamingPreferences {
  const prefs = ((profile?.preferences as Record<string, unknown> | null) || {}).documentNaming;
  if (!prefs || typeof prefs !== "object") {
    return DEFAULT_DOCUMENT_NAMING_PREFERENCES;
  }
  return resolveDocumentNamingPreferences(prefs as Partial<DocumentNamingPreferences>);
}

export function shouldUseCustomDocumentNaming(
  preferences: Partial<DocumentNamingPreferences> | null | undefined
): boolean {
  return resolveDocumentNamingPreferences(preferences).enabled;
}

export function shouldUseDescriptorOnlyTitle(
  preferences: Partial<DocumentNamingPreferences> | null | undefined
): boolean {
  const prefs = resolveDocumentNamingPreferences(preferences);
  return prefs.enabled && !prefs.includeSubjectPrefix;
}

export function applyDocumentNamingPreferences(
  baseTitle: string,
  options?: {
    topicTitle?: string | null;
    createdAt?: Date | string | number | null;
    preferences?: Partial<DocumentNamingPreferences> | null;
  }
): string {
  const normalizedBase = removeExistingDateSuffix(normalizeWhitespace(baseTitle));
  if (!normalizedBase) return "";

  const prefs = resolveDocumentNamingPreferences(options?.preferences);
  if (!prefs.enabled) return normalizedBase;

  const { subject, descriptor: rawDescriptor } = splitSuggestedTitle(normalizedBase);
  const normalizedTopic = prefs.includeTopicTitle ? normalizeTopicTitle(options?.topicTitle) : null;
  const subjectTokens = prefs.includeSubjectPrefix ? parseSubjectTokens(subject) : [];
  const filteredSubjectTokens = subjectTokens.filter(
    (token) => token.toLowerCase() !== normalizedTopic?.toLowerCase()
  );
  const prefixTokens = [normalizedTopic, ...filteredSubjectTokens].filter(Boolean) as string[];
  const descriptor = normalizeDescriptor(rawDescriptor || normalizedBase);

  let formatted = prefixTokens.length > 0 ? `${prefixTokens.join(" / ")}: ${descriptor}` : descriptor;
  if (!formatted) {
    formatted = normalizedBase;
  }

  if (prefs.appendCreatedDate) {
    formatted = `${formatted} (${formatCreatedDate(options?.createdAt)})`;
  }

  return formatted;
}
